"use client";

import { useCallback, useMemo } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { showToast } from "@/components/ui/sonner";
import {
  isPermissionDenied,
  getPermissionErrorMessage,
} from "@/lib/permissions";
import { useOpenArchiveProject } from "../components/list/archive-confirm";
import { useOpenManageProject } from "../components/list/manage-url";
import { useProjectSwitch } from "./use-project-switch";

export interface UseProjectActionsOptions {
  title?: string;
  coverImage?: string | null;
  /** When true, select navigates to /jobs without calling the select API. */
  isSelected?: boolean;
}

export interface UseProjectActionsResult {
  select: () => void;
  manage: () => void;
  archive: () => void;
  isSelecting: boolean;
}

export function useProjectActions(
  projectId: string,
  options?: UseProjectActionsOptions,
): UseProjectActionsResult {
  const queryClient = useQueryClient();
  const router = useRouter();
  const openManage = useOpenManageProject();
  const openArchive = useOpenArchiveProject();
  const isSelected = options?.isSelected ?? false;

  const { switchProject, isPending: isSelecting } = useProjectSwitch({
    onSuccess: () => {
      router.push("/jobs");
    },
    onError: (err: Error) => {
      if (isPermissionDenied(err)) {
        queryClient.invalidateQueries({ queryKey: ["projects"] });
        showToast.error("Access denied", {
          description: getPermissionErrorMessage(err),
        });
      } else {
        showToast.error("Failed to select project", {
          description: err.message,
        });
      }
    },
  });

  const select = useCallback(() => {
    // Already the active project — skip the redundant select mutation / full
    // project-switch reset and just open Jobs.
    if (isSelected) {
      router.push("/jobs");
      return;
    }
    switchProject(
      projectId,
      options?.title
        ? {
            id: projectId,
            title: options.title,
            cover_image_url: options.coverImage ?? null,
          }
        : undefined,
    );
  }, [
    isSelected,
    router,
    switchProject,
    projectId,
    options?.title,
    options?.coverImage,
  ]);

  const manage = useCallback(() => {
    openManage(projectId);
  }, [openManage, projectId]);

  const archive = useCallback(() => {
    openArchive(projectId);
  }, [openArchive, projectId]);

  return useMemo(
    () => ({ select, manage, archive, isSelecting }),
    [select, manage, archive, isSelecting],
  );
}
